import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	Button,
	InputAdornment,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useExtractionConfigurationsQuery } from "Configurations/Api/Queries/useExtractionConfigurationsQuery";
import { CreateExtractionConfigurationDialog } from "Configurations/Components/CreateExtractionConfigurationDialog";
import { ExtractionConfigurationsTable } from "Configurations/Components/ExtractionConfigurationsTable";
import { ImportConfigurationsDialog } from "Configurations/Components/ImportConfigurationsDialog";
import type { ImportedConfig } from "Configurations/Types/ImportedConfig";
import type { ExtractionConfigurationListItemDto } from "Infrastructure/Api/Api";
import { AuthenticatedLayout } from "Shared/Layouts/AuthenticatedLayout";
import { type FC, useRef, useState } from "react";
import { toast } from "sonner";

export const ConfigurationsPage: FC = () => {
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [editConfig, setEditConfig] = useState<
		ExtractionConfigurationListItemDto | undefined
	>(undefined);
	const [searchQuery, setSearchQuery] = useState("");
	const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
	const [importedConfigs, setImportedConfigs] = useState<ImportedConfig[]>([]);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const { data } = useExtractionConfigurationsQuery();
	const configurations = data?.items ?? [];

	const handleCreateClick = () => {
		setEditConfig(undefined);
		setIsDialogOpen(true);
	};

	const handleEditClick = (config: ExtractionConfigurationListItemDto) => {
		setEditConfig(config);
		setIsDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setIsDialogOpen(false);
		setEditConfig(undefined);
	};

	const handleExport = () => {
		try {
			// Prepare data for export (remove IDs and timestamps that shouldn't be imported)
			const exportData = configurations.map((config) => ({
				name: config.name,
				prompt: config.prompt,
				jsonSchema: config.jsonSchema,
				connectorProvider: config.connectorProvider,
				tenantCode: config.tenantCode,
				pageLimit: config.pageLimit,
				forceOcr: config.forceOcr,
				identificationHints: config.identificationHints,
			}));

			// Create blob and download
			const blob = new Blob([JSON.stringify(exportData, null, 2)], {
				type: "application/json",
			});
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `extraction-configurations-${new Date().toISOString().split("T")[0]}.json`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);

			toast.success(`Exported ${configurations.length} configuration(s)`);
		} catch (error) {
			console.error("Export failed:", error);
			toast.error("Failed to export configurations");
		}
	};

	const handleImportClick = () => {
		fileInputRef.current?.click();
	};

	const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;

		try {
			const text = await file.text();
			const importedConfigs = JSON.parse(text);

			if (!Array.isArray(importedConfigs)) {
				toast.error("Invalid format: Expected an array of configurations");
				return;
			}

			// Validate the structure
			const isValid = importedConfigs.every(
				(config) =>
					typeof config.name === "string" &&
					typeof config.prompt === "string" &&
					typeof config.jsonSchema === "string",
			);

			if (!isValid) {
				toast.error(
					"Invalid format: Missing required fields (name, prompt, jsonSchema)",
				);
				return;
			}

			// Open import dialog with the configurations
			setImportedConfigs(importedConfigs);
			setIsImportDialogOpen(true);
		} catch (error) {
			console.error("Import failed:", error);
			toast.error(
				"Failed to import configurations. Please check the file format.",
			);
		} finally {
			// Reset file input
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		}
	};

	const handleCloseImportDialog = () => {
		setIsImportDialogOpen(false);
		setImportedConfigs([]);
	};

	return (
		<AuthenticatedLayout title="Configurations">
			<Box>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="center"
					mb={2}
				>
					<Typography variant="h4">Extraction Configurations</Typography>
					<Stack direction="row" spacing={1}>
						<Button
							variant="outlined"
							startIcon={<FileUploadIcon />}
							onClick={handleImportClick}
						>
							Import
						</Button>
						<Button
							variant="outlined"
							startIcon={<FileDownloadIcon />}
							onClick={handleExport}
							disabled={configurations.length === 0}
						>
							Export
						</Button>
						<Button
							variant="contained"
							color="primary"
							startIcon={<AddIcon />}
							onClick={handleCreateClick}
						>
							Create
						</Button>
					</Stack>
				</Stack>
				<Box mb={2}>
					<TextField
						fullWidth
						placeholder="Search by configuration name or tenant..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
						}}
						size="small"
					/>
				</Box>
				<ExtractionConfigurationsTable
					onEdit={handleEditClick}
					searchQuery={searchQuery}
				/>
				{isDialogOpen && (
					<CreateExtractionConfigurationDialog
						open={isDialogOpen}
						onClose={handleCloseDialog}
						editData={editConfig}
					/>
				)}
				{isImportDialogOpen && (
					<ImportConfigurationsDialog
						open={isImportDialogOpen}
						onClose={handleCloseImportDialog}
						configurations={importedConfigs}
					/>
				)}
				{/* Hidden file input for import */}
				<input
					ref={fileInputRef}
					type="file"
					accept="application/json"
					style={{ display: "none" }}
					onChange={handleImport}
				/>
			</Box>
		</AuthenticatedLayout>
	);
};
